import type { SdkMessage, TelemetryEvent, TelemetryKind } from './types';
import { IDX_ATTR } from './extractor';
import { roleOf, textOf } from './dom';
import { redact } from './redact';
import { log } from './logger';

type Sender = (message: SdkMessage) => void;

let send: Sender | null = null;
let appId = '';
let sessionId = '';
let clickBound = false;
const buffer: TelemetryEvent[] = [];
const MAX_BUFFER = 50;

export function configureTelemetry(options: {
  appId: string;
  sessionId: string;
  send: Sender;
}): void {
  appId = options.appId;
  sessionId = options.sessionId;
  send = options.send;
  flush();
}

export function emit(kind: TelemetryKind, payload: Record<string, unknown> = {}): void {
  const event: TelemetryEvent = {
    app_id: appId,
    session_id: sessionId,
    ts: Math.floor(Date.now() / 1000),
    kind,
    route: window.location.pathname,
    payload,
  };

  if (!send) {
    if (buffer.length < MAX_BUFFER) buffer.push(event);
    return;
  }

  try {
    send({ type: 'event', event });
  } catch (error) {
    log.warn('telemetry emit failed', error);
  }
}

function flush(): void {
  if (!send) return;
  while (buffer.length > 0) {
    const event = buffer.shift();
    if (!event) break;
    event.app_id = appId;
    event.session_id = sessionId;
    try {
      send({ type: 'event', event });
    } catch {
      break;
    }
  }
}

function onDocumentClick(nativeEvent: Event): void {
  const target = nativeEvent.target;
  if (!(target instanceof Element)) return;
  const actionable = target.closest(`[${IDX_ATTR}], a[href], button, [role="button"]`);
  if (!actionable) return;

  emit('element_click', {
    index: Number(actionable.getAttribute(IDX_ATTR)) || null,
    role: roleOf(actionable),
    text: redact(textOf(actionable)),
  });
}

export function startTelemetry(sdkRoot: Element | null): void {
  if (clickBound) return;
  clickBound = true;
  document.addEventListener(
    'click',
    (nativeEvent) => {
      const target = nativeEvent.target;
      if (sdkRoot && target instanceof Node && sdkRoot.contains(target)) return;
      onDocumentClick(nativeEvent);
    },
    { capture: true, passive: true },
  );
}

export function stopTelemetry(): void {
  clickBound = false;
  send = null;
  buffer.length = 0;
}
